import { Injectable } from '@angular/core';
import { Platform } from 'ionic-angular/platform/platform';
import { ToastController, AlertController, ModalController } from 'ionic-angular';
import { UserProvider } from '../user/user';
import { Observable } from 'rxjs/Observable';
import { combineLatest } from 'rxjs/observable/combineLatest';
import { FCM, NotificationData } from '@ionic-native/fcm';
import { SignalsProvider } from '../signals/signals';
import { SettingsProvider } from '../settings/settings';
import { UserSettings } from '../../models/UserSettings';
import { GradesProvider } from '../grades/grades';
import { PulseProvider } from '../pulse/pulse';
import { AddSignalComponent } from '../../components/add-signal/add-signal';
import { AddSignalStocksComponent } from '../../components/add-signal-stocks/add-signal-stocks';
import { ReportsProvider } from '../reports/reports';
/*
  Generated class for the FcmProvider provider.

  See https://angular.io/guide/dependency-injection for more info on providers
  and Angular DI.
*/
@Injectable()
export class FcmProvider {
    appIsOpen = true;
    hasAccess: Observable<boolean> = null;
    isAdmin: Observable<boolean>;
    constructor(
        private platform: Platform,
        private user: UserProvider,
        private toast: ToastController,
        private FCM: FCM,
        public alert: AlertController,
        private signalCtrl: SignalsProvider,
        private settingsCtrl: SettingsProvider,
        private gradeCtrl: GradesProvider,
        private reportCtrl: ReportsProvider,
        private pulseCtrl: PulseProvider,
        private modalCtrl: ModalController
    ) {
        if (!this.platform.is('cordova')) return; //Only native

        this.hasAccess = this.user.hasAccess();
        this.isAdmin = this.user.isAdmin();

        platform.ready().then(val => {
            this.initFCM();
        })
    }

    private initFCM() {
        combineLatest([
            this.hasAccess, 
            this.isAdmin,
            this.settingsCtrl.getUserSettings()
        ]).subscribe(async ([hasAccess, isAdmin, userSettings]) => {
            let settings: UserSettings = userSettings; 
            try {
                if (hasAccess || isAdmin) {
                    if(settings.enablePushNotificationsSignals) await this.FCM.subscribeToTopic('signal');
                    else await this.FCM.unsubscribeFromTopic('signal');
                    await this.setADumbassTimeout();

                    if(settings.enablePushNotificationsGrades) await this.FCM.subscribeToTopic('grade');
                    else await this.FCM.unsubscribeFromTopic('grade');
                    await this.setADumbassTimeout();

                    if(settings.enablePushNotificationsReports) await this.FCM.subscribeToTopic('report');
                    else await this.FCM.unsubscribeFromTopic('report');
                    await this.setADumbassTimeout();

                    if(settings.enablePushNotificationsSignalsStocks) await this.FCM.subscribeToTopic('signal-stocks');
                    else await this.FCM.unsubscribeFromTopic('signal-stocks');
                    await this.setADumbassTimeout();

                    if(settings.enablePushNotificationsGradesStocks) await this.FCM.subscribeToTopic('grade-stocks');
                    else await this.FCM.unsubscribeFromTopic('grade-stocks');
                    await this.setADumbassTimeout();

                    if(settings.enablePushNotificationsReportsStocks) await this.FCM.subscribeToTopic('report-stocks');
                    else await this.FCM.unsubscribeFromTopic('report-stocks');
                    await this.setADumbassTimeout();

                    if(settings.enablePushNotificationsStream) await this.FCM.subscribeToTopic('stream');
                    else await this.FCM.unsubscribeFromTopic('stream');

                    await this.setADumbassTimeout();
                } else {    
                    await this.FCM.unsubscribeFromTopic('signal');
                    await this.setADumbassTimeout();
                    await this.FCM.unsubscribeFromTopic('grade');
                    await this.setADumbassTimeout();
                    await this.FCM.unsubscribeFromTopic('report');
                    await this.setADumbassTimeout();
                    await this.FCM.unsubscribeFromTopic('signal-stocks');
                    await this.setADumbassTimeout();
                    await this.FCM.unsubscribeFromTopic('grade-stocks');
                    await this.setADumbassTimeout();
                    await this.FCM.unsubscribeFromTopic('report-stocks');
                    await this.setADumbassTimeout();
                    await this.FCM.unsubscribeFromTopic('stream');
                    await this.setADumbassTimeout();
                }

                if(settings && settings.enablePushNotificationsPulse) await this.FCM.subscribeToTopic('pulse');
                else await this.FCM.unsubscribeFromTopic('pulse');

            } catch (error) {
                console.log('error', error);
            }
        });

        this.FCM.onNotification().subscribe((data: NotificationData) => {
            this.handleNotification(data);
        }, error => {
            console.log(error);
        })
    }

    public handleNotification(data: NotificationData) {
        const type = data.type;
        if (data.wasTapped) {
            if(type == 'grade'){
                this.openGrade(data);
            }else if(type == 'pulse'){
                const slug = data.slug;
                this.pulseCtrl.openPost(slug);
            }else{
                this.openSignal(data);
            }
        } else {
            let title;
            let subTitle;

            if (this.platform.is('android')) {
                title = data.title;
                subTitle = data.body;
            } else {
                title = data.aps.alert.title;
                subTitle = data.aps.alert.body;
            }

            this.alert.create({
                title: title,
                subTitle: subTitle,
                message: `Would you like to see it now?`,
                buttons: [
                    {
                        text: 'No',
                        role: 'cancel'
                    },
                    {
                        text: 'Yes',
                        handler: () => {
                            if(type == 'grade'){
                                this.openGrade(data);
                            }else if(type == 'pulse'){
                                const slug = data.slug;
                                this.pulseCtrl.openPost(slug);
                            }else if(type == 'report'){
                                this.openReport(data);
                            }else{
                                this.openSignal(data);
                            }
                            return true;
                        }
                    }
                ]
            }).present();
        };
    }

    public async openSignal(data: NotificationData) {
        const signalID = data.signalID;
        const isStock = (data.isStock == 'true');

        try {
            let signal = await this.signalCtrl.getSignalByID(signalID, isStock);
            let modal = null;
            
            if(signal.data.isStock){
                modal = this.modalCtrl.create(AddSignalStocksComponent, {signal});
            }else{
                modal = this.modalCtrl.create(AddSignalComponent, {signal});
            }
            
            modal.present();
        } catch (error) {
            console.error(error);
        }
    }

    private async openGrade(data: NotificationData){
        const gradeId = data.gradeId;
        const isStock = (data.isStock == 'true');

        try {
            let grade = await this.gradeCtrl.getGradeById(gradeId, isStock);
            if (grade) this.gradeCtrl.openGrade(grade);
        } catch (error) {
            console.log(error);
        }
    }

    private async openReport(data: NotificationData){
        const reportId = data.reportId;
        const isStock = (data.isStock == 'true');
        try {
            let report = await this.reportCtrl.getReportById(reportId, isStock);
            if (report) this.reportCtrl.openReport(report);
        } catch (error) {
            console.log(error);
        }
    }

    private setADumbassTimeout(): Promise<void>{
        return new Promise((resolve, reject) => {
            setTimeout(resolve, 1000);
        });
    }
    
}



// WEBPACK FOOTER //
// ./src/providers/fcm/fcm.ts